import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useEmployeeSession } from '../EmployeeSessionProvider';
import { subscribePosStream } from '../../services/pos-stream/pos-stream.service';

const tenantIdFromPathname = (pathname: string): string | null => {
  const match = pathname.match(/^\/t\/([^/]+)\/(?:pos|kds|dashboard|backoffice|maintenance)(?:\/|$)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const TenantMaintenanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshAvailableTenants } = useAuth();
  const { hydrateEmployeeState } = useEmployeeSession();
  const tenantId = useMemo(() => tenantIdFromPathname(location.pathname), [location.pathname]);
  const [employeeSession, setEmployeeSession] = useState<string | null>(null);
  const reconcileInFlightRef = useRef(false);
  const MAINTENANCE_POLL_MS = 5000;

  const syncEmployeeSession = useCallback((): string | null => {
    if (!tenantId) {
      setEmployeeSession(null);
      return null;
    }
    const next = hydrateEmployeeState(tenantId)?.employeeSession ?? null;
    setEmployeeSession(next);
    return next;
  }, [hydrateEmployeeState, tenantId]);

  useEffect(() => {
    void location.pathname;
    syncEmployeeSession();
  }, [location.pathname, syncEmployeeSession]);

  const reconcileMaintenanceMode = useCallback(async () => {
    if (!tenantId || user?.role === 'super_admin' || reconcileInFlightRef.current) {
      return;
    }

    reconcileInFlightRef.current = true;
    try {
      const tenants = await refreshAvailableTenants();
      const maintenanceMode = tenants.some((tenant) => tenant.tenantId === tenantId && tenant.maintenanceMode);
      const maintenancePath = `/t/${encodeURIComponent(tenantId)}/maintenance`;
      if (!maintenanceMode && location.pathname === maintenancePath) {
        navigate(`/t/${encodeURIComponent(tenantId)}/pos`, { replace: true });
      }
      if (maintenanceMode && location.pathname !== maintenancePath) {
        navigate(maintenancePath, { replace: true });
      }
    } catch {
      // Keep the current screen if the lightweight state check is temporarily unavailable.
    } finally {
      reconcileInFlightRef.current = false;
    }
  }, [location.pathname, navigate, refreshAvailableTenants, tenantId, user?.role]);

  useEffect(() => {
    if (!tenantId || user?.role === 'super_admin') {
      return;
    }

    // A suspended background tab can miss the maintenance SSE event. Reconcile immediately on
    // return; while on the maintenance screen, poll lightly so it can always release itself.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncEmployeeSession();
        void reconcileMaintenanceMode();
      }
    };
    window.addEventListener('pageshow', onVisibilityChange);
    window.addEventListener('focus', onVisibilityChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    void reconcileMaintenanceMode();

    const maintenancePath = `/t/${encodeURIComponent(tenantId)}/maintenance`;
    const pollId = location.pathname === maintenancePath
      ? window.setInterval(() => {
        syncEmployeeSession();
        void reconcileMaintenanceMode();
      }, MAINTENANCE_POLL_MS)
      : null;

    return () => {
      window.removeEventListener('pageshow', onVisibilityChange);
      window.removeEventListener('focus', onVisibilityChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (pollId !== null) {
        window.clearInterval(pollId);
      }
    };
  }, [location.pathname, reconcileMaintenanceMode, syncEmployeeSession, tenantId, user?.role]);

  useEffect(() => {
    if (!tenantId || !employeeSession || user?.role === 'super_admin') {
      return;
    }

    const maintenancePath = `/t/${encodeURIComponent(tenantId)}/maintenance`;
    return subscribePosStream(
      tenantId,
      employeeSession,
      (event) => {
        if (event.type !== 'tenant.maintenance-mode-changed') {
          return;
        }

        if (event.maintenanceMode && location.pathname !== maintenancePath) {
          navigate(maintenancePath, { replace: true });
        }
        if (!event.maintenanceMode && location.pathname === maintenancePath) {
          navigate(`/t/${encodeURIComponent(tenantId)}/pos`, { replace: true });
        }
      },
      undefined,
      undefined,
      () => {
        void reconcileMaintenanceMode();
      },
      () => {
        void reconcileMaintenanceMode();
      },
    );
  }, [employeeSession, location.pathname, navigate, reconcileMaintenanceMode, tenantId, user?.role]);

  return <>{children}</>;
};
